import { Tooltip } from "antd";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import OperationStatus from "../../../../components/Operation/OperationStatus";
import { datetime } from "../../../../util/library/datetime";
import { selectSetting } from "../../../../util/store/selectors";
import { KEY_TAB, OP_STATUS, SHORT_DATETIME_FORMAT } from "../../../../util/varibles/constants";
import { checkZero } from "../../../../util/varibles/global";
import { IIdName2, IServiceRequest } from "../../../../util/varibles/interface";
import { PlanSetting } from "../../../../util/varibles/userSetting";
import { selectPlan } from "../../constants";
import Cards from "../_components/Cards";
import stylesContainer from "../style.module.scss";
import Card from "./Card";
import HeaderTab from "./HeaderTab";
import OpStatus from "./OpStatus";
import styles from './style.module.scss';

export enum SERVICE_REQUEST_TYPE {
    NEW = 'new',
    PLANNED = 'planned',
    ONGOING = 'ongoing',
    FINISHED = 'finished',
}

export const serviceRequestType: Record<string, IIdName2<SERVICE_REQUEST_TYPE> & { background?: string, color?: string }> = {
    [SERVICE_REQUEST_TYPE.NEW]: {
        id: SERVICE_REQUEST_TYPE.NEW,
        name: 'New',
        background: '#DDEEED',
        color: 'var(--fontActive)'
    },
    [SERVICE_REQUEST_TYPE.PLANNED]: {
        id: SERVICE_REQUEST_TYPE.PLANNED,
        name: 'Planned',
        background: '#5DACA5'
    },
    [SERVICE_REQUEST_TYPE.ONGOING]: {
        id: SERVICE_REQUEST_TYPE.ONGOING,
        name: 'Ongoing',
        background: '#72A64E'
    },
    [SERVICE_REQUEST_TYPE.FINISHED]: {
        id: SERVICE_REQUEST_TYPE.FINISHED,
        name: 'Completed',
        background: '#ABA89C'
    },
};

const getRequestStatuses = (operations: IServiceRequest['operations'] = [], vessel_resources: IServiceRequest['vessel_resources'] = []) => {
    const statuses = new Set<SERVICE_REQUEST_TYPE>();
    if (vessel_resources.length > 0) {
        statuses.add(SERVICE_REQUEST_TYPE.PLANNED)
    } else if (operations.length === 0)
        return [SERVICE_REQUEST_TYPE.NEW]

    for (const operation of operations) {
        switch (operation.status) {
            case OP_STATUS.PROCESSING:
                statuses.add(SERVICE_REQUEST_TYPE.ONGOING);
                break;

            case OP_STATUS.FINISHED:
                statuses.add(SERVICE_REQUEST_TYPE.FINISHED);
                break;

            default:
                statuses.add(SERVICE_REQUEST_TYPE.PLANNED);
                break;
        }
    }

    return Array.from(statuses);
};

export default function ServiceRequest() {
    const loading = useSelector(selectPlan.loadingServiceRequest);
    const { [PlanSetting.SERVICE_REQUESTS_STATUS]: statusFilter } = useSelector(selectSetting([PlanSetting.SERVICE_REQUESTS_STATUS]))
    const serviceRequests = useSelector(selectPlan.serviceRequests);
    const [{ searchValue }, setForm] = useState({ searchValue: "" });
    const statusSet = useMemo(() => {
        const real = (statusFilter || []).filter((key: string) => serviceRequestType[key]);
        return new Set(real)
    }, [statusFilter]);

    const source = useMemo(() => {
        return serviceRequests.filter(item => {
            const { name, description = "", id, operations = [], vessel_resources = [] } = item;
            const isSearch = id.toLowerCase().indexOf((searchValue || '').toLowerCase()) !== -1
                || name.toLowerCase().indexOf((searchValue || '').toLowerCase()) !== -1
                || description.toLowerCase().indexOf((searchValue || '').toLowerCase()) !== -1;

            const statusFilter = statusSet.size === 0 || getRequestStatuses(operations, vessel_resources).some(status => statusSet.has(status))
            return isSearch && statusFilter;
        })
    }, [serviceRequests, searchValue, statusSet])

    return <div id={KEY_TAB.SERVICE_REQUEST} className={stylesContainer['wrapper-tab']}>
        <HeaderTab onSearch={value => setForm(prev => ({ ...prev, searchValue: value }))} />
        <div className={stylesContainer['tab-body']}>
            <Cards<IServiceRequest>
                data={source}
                loading={loading}
                child={{
                    View: ({ data }) => {
                        const { operations = [], start_time, finish_time, vessel_resources = [] } = data;
                        return <Card data={data} key={data.id}>
                            <div className='flex flex-wrap gap-1 [&>*]:text-[13px]'>
                                <div className="leading-[20px] text-[13px] font-[600] px-[5px]">
                                    {data.id}
                                </div>
                                {operations.length > 0
                                    ? operations?.map(item => <OpStatus key={item.id} data={item} />)
                                    : vessel_resources.length > 0
                                        ? <Tooltip title="Already assigned on Havbruksloggen">
                                            <div className={`${styles.opStatus} cursor-default`} style={{ background: serviceRequestType[SERVICE_REQUEST_TYPE.PLANNED].background }}>
                                                {serviceRequestType[SERVICE_REQUEST_TYPE.PLANNED].name}
                                            </div>
                                        </Tooltip>
                                        : <OperationStatus status={OP_STATUS.NEW} />}
                                <div className="font-[600] text-[14px] uppercase whitespace-nowrape">
                                    {data.prod_area ? `P${checkZero(data.prod_area)} | ` : ""} {data.name}
                                </div>
                            </div>
                            <div className="text-[14px] mt-[2px]">
                                {vessel_resources.join(', ')}
                            </div>
                            <div className="flex items-center h-[20px] gap-1 text-[14px]">
                                <div>{start_time ? datetime(start_time).format(SHORT_DATETIME_FORMAT) : '--'}</div>
                                <div className="arrow-right" />
                                <div>{finish_time ? datetime(finish_time).format(SHORT_DATETIME_FORMAT) : '--'}</div>
                            </div>
                            <div className="text-[14px]">
                                {data.description}
                            </div>
                        </Card>
                    }
                }}
            />
        </div>
    </div>
}